import { useState } from "react";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import axios from "axios";
import {
  Vault, FolderLock, Lightning, Kanban, BookBookmark, Sparkle, Target,
  Scales, Heart, UserCircle, Briefcase, ArrowRight, EnvelopeSimple,
  PaperPlaneTilt, CheckCircle,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  {
    id: "vault",
    icon: Vault,
    accent: "var(--bolt-terracotta)",
    bg: "bg-[var(--pastel-peach)]",
    title: "The Vault",
    blurb: "Your single source of truth for everything employers ask for.",
    how: [
      "Use the chronological sub-tabs (Work, Education, References, Skills) to add entries one by one.",
      "Click 'edit' on any entry and the editor opens right where the entry lives — no scrolling to a separate form.",
      "Use the 'i know the exact day' toggle for precise dates, or leave it off for month + year only.",
      "Skills are comma-separated and render as colour-coded chips automatically.",
      "Drag the ⋮⋮ handle to reorder. The order you set is the order autofill uses.",
    ],
  },
  {
    id: "documents",
    icon: FolderLock,
    accent: "var(--bolt-cobalt)",
    bg: "bg-[var(--bolt-sky)]",
    title: "Documents",
    blurb: "CVs, transcripts, certificates, IDs — uploaded once, tagged & dated.",
    how: [
      "Upload PDFs / images and tag them (CV, transcript, certificate, ID, other).",
      "Add an expiry date — JobVault will email you 30, 14 and 3 days before anything lapses.",
      "Copy a direct download link from any document to paste into a Workday-style upload field.",
    ],
  },
  {
    id: "tracker",
    icon: Kanban,
    accent: "var(--bolt-plum)",
    bg: "bg-[var(--bolt-blush)]",
    title: "Application Tracker",
    blurb: "Kanban board + colourful calendar with deadlines, interviews & offers.",
    how: [
      "Add an application (company + role) — drag cards between Applied / Interview / Offer / Rejected.",
      "Switch to the Calendar view for the bird's-eye look at upcoming dates colour-coded by event type.",
      "After any interview, open the post-mortem drawer to capture what went well, what to fix, and how you felt.",
    ],
  },
  {
    id: "autofill",
    icon: Lightning,
    accent: "var(--bolt-lime)",
    bg: "bg-[var(--bolt-butter)]",
    title: "Autofill (Chrome Extension)",
    blurb: "One click to fill any job application form with your vault.",
    how: [
      "Download the extension zip from /autofill and install it as an unpacked extension in Chrome.",
      "Click the JobVault icon, sign in, leave the backend URL as the default unless you're testing a preview build.",
      "On any job page, hit 'autofill this page' — it'll match Workday, Greenhouse, Lever, Ashby and most ATS portals.",
      "Use 'scan this job ad → star rating' for an instant culture-fit check on the page you're viewing.",
    ],
  },
  {
    id: "examples",
    icon: BookBookmark,
    accent: "var(--bolt-sage)",
    bg: "bg-[var(--pastel-mint)]",
    title: "Personal Bank",
    blurb: "Your reusable bank of STAR stories — used by Vault Coach for cover-letter rewrites and interview prep.",
    how: [
      "Add an example: pick the competency, write your Situation / Task / Action / Result.",
      "Tag examples with skills so autofill (and Vault Coach) can match them to the right interview question.",
      "Copy any example as plain text or Markdown — Vault Coach pulls from this bank automatically.",
    ],
  },
  {
    id: "coach",
    icon: Sparkle,
    accent: "var(--bolt-mustard)",
    bg: "bg-[var(--bolt-butter)]",
    title: "Vault Coach",
    blurb: "Three AI tools, one coach — CV tailoring, cover letter checking, interview prep.",
    twoTabs: [
      {
        title: "CV Tailor",
        desc: "Paste a job description → AI rewrites your existing work bullets to match its language, without inventing skills you don't have. Download the tailored version as a .docx.",
      },
      {
        title: "Cover Letter Checker",
        desc: "Paste your cover letter + the JD → AI scores ATS keyword match, AI-likelihood ('sounds like ChatGPT'), and clichés. Rewrite suggestions are pulled from your Personal Bank STAR stories.",
      },
      {
        title: "Interview Prep",
        desc: "Tell us the target role and optional JD → AI generates 6-8 behavioural + role-specific questions, each with a suggested approach pulled from your vault. Flags gaps in your stories too.",
      },
    ],
    how: [
      "Three tiles at the top of the Vault Coach page — click to switch.",
      "All three tools run on Claude. Each call uses a few cents of your Universal LLM credits.",
      "Interview Prep is most useful AFTER you've populated your Vault and a few Personal Bank entries.",
    ],
  },
  {
    id: "compare",
    icon: Scales,
    accent: "var(--bolt-cobalt)",
    bg: "bg-[var(--bolt-sky)]",
    title: "Compare Jobs",
    blurb: "Side-by-side: culture, skills, experience and compensation — with community medians.",
    how: [
      "Four tabs: Culture (saved barometer analyses), Skills, Experience, Compensation.",
      "Add a row per role you're considering — JobVault crunches a community median across all users so you know if a salary band is fair.",
      "The Culture Barometer that used to live in AI Coach is now its own Compare tab.",
    ],
  },
  {
    id: "wellbeing",
    icon: Heart,
    accent: "var(--bolt-blush)",
    bg: "bg-[var(--bolt-blush)]",
    title: "Wellbeing Corner",
    blurb: "Job hunting is brutal. This is the corner that reminds you you're a human first.",
    how: [
      "Daily affirmation rotates on every visit — paste into the group chat when you need it.",
      "Set a 'rejection-streak threshold' to get a gentle pep-talk email when applications go quiet.",
    ],
  },
  {
    id: "profile",
    icon: UserCircle,
    accent: "var(--bolt-sky)",
    bg: "bg-[var(--bolt-sky)]",
    title: "Profile & Privacy",
    blurb: "Where you control who sees what, and how often we email you.",
    how: [
      "Toggle email reminders on/off in one click.",
      "Generate a public share link with redacted refs to send to recruiters.",
      "Export everything as JSON / Markdown / .docx — your data is always yours.",
      "Delete your account from the bottom of the page (right to erasure under UK GDPR).",
    ],
  },
];

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true); setErr("");
    try {
      await axios.post(`${API}/api/contact/send`, form);
      setSent(true);
      toast.success("message sent — we'll reply within 48h");
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setErr(typeof detail === "string" ? detail : "couldn't send — try again");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="card-hard rounded-md p-6 bg-[var(--pastel-mint)]" data-testid="contact-sent">
        <CheckCircle size={28} weight="duotone" className="mb-2"/>
        <h3 className="font-display text-2xl font-semibold tracking-tight">message in the post.</h3>
        <p className="text-sm mt-2 text-[var(--brand-ink)]/75">we read everything that lands in <b>jobvaultadmin@gmail.com</b> and reply within 48h — usually a lot sooner.</p>
        <button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
          data-testid="contact-send-another"
          className="btn-hard bg-white rounded-md px-4 py-2 text-sm mt-4">send another</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-hard rounded-md p-6 bg-white" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-tiny block mb-1.5" htmlFor="contact-name">your name</label>
          <input id="contact-name" data-testid="contact-name" required maxLength={120}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full card-hard rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[var(--brand-primary)]" />
        </div>
        <div>
          <label className="label-tiny block mb-1.5" htmlFor="contact-email">your email</label>
          <input id="contact-email" data-testid="contact-email" required type="email"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="so we can reply"
            className="w-full card-hard rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[var(--brand-primary)]" />
        </div>
      </div>
      <div className="mt-4">
        <label className="label-tiny block mb-1.5" htmlFor="contact-subject">subject <span className="text-[var(--brand-muted)] normal-case tracking-normal">(optional)</span></label>
        <input id="contact-subject" data-testid="contact-subject" maxLength={200}
          value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
          placeholder="e.g. feature idea, bug report, partnership"
          className="w-full card-hard rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-[var(--brand-primary)]" />
      </div>
      <div className="mt-4">
        <label className="label-tiny block mb-1.5" htmlFor="contact-message">message</label>
        <textarea id="contact-message" data-testid="contact-message" required minLength={10} maxLength={4000}
          value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="what's on your mind?"
          className="w-full card-hard rounded-md px-3 py-2 min-h-[140px] bg-white focus:ring-2 focus:ring-[var(--brand-primary)]" />
        <p className="text-[10px] font-mono-d text-[var(--brand-muted)] mt-1">{form.message.length}/4000</p>
      </div>
      {err && (
        <div className="card-hard rounded-md p-3 bg-[var(--pastel-peach)] text-sm mt-4" data-testid="contact-error">{err}</div>
      )}
      <button type="submit" disabled={sending} data-testid="contact-submit"
        className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-5 py-2.5 text-sm font-medium mt-5 inline-flex items-center gap-2 disabled:opacity-60 hover:bg-[var(--brand-ink)]">
        {sending ? "sending…" : <>send message <PaperPlaneTilt size={14} weight="bold"/></>}
      </button>
    </form>
  );
}

export default function Guide() {
  return (
    <AppShell title="Guide & Help">
      <div className="max-w-4xl">
        <p className="label-tiny mb-2">/guide</p>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-3">
          how to use <span className="text-[var(--bolt-terracotta)]">JobVault</span>.
        </h1>
        <p className="text-[var(--brand-ink)]/75 text-lg max-w-2xl">
          a tour of every corner of your vault. skim the table of contents, jump to anything you missed.
        </p>

        {/* Table of contents */}
        <div className="card-hard rounded-md p-5 bg-[var(--bolt-butter)] mt-6" data-testid="guide-toc">
          <p className="label-tiny mb-3">/contents</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <a key={s.id} href={`#${s.id}`}
                  data-testid={`toc-${s.id}`}
                  className="flex items-center gap-2 text-sm hover:bg-white px-2 py-1.5 rounded-md transition">
                  <Icon size={16} weight="duotone" />
                  <span>{s.title}</span>
                </a>
              );
            })}
            <a href="#contact" data-testid="toc-contact"
              className="flex items-center gap-2 text-sm hover:bg-white px-2 py-1.5 rounded-md transition">
              <EnvelopeSimple size={16} weight="duotone" />
              <span>Contact us</span>
            </a>
          </div>
        </div>

        {/* Feature sections */}
        <div className="space-y-6 mt-10">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <section key={s.id} id={s.id}
                data-testid={`guide-${s.id}`}
                className={`card-hard rounded-md p-6 ${s.bg} relative overflow-hidden scroll-mt-24`}>
                <span aria-hidden="true"
                  className="absolute left-0 top-0 bottom-0 w-1.5"
                  style={{ background: s.accent }}/>
                <div className="flex items-start gap-3">
                  <Icon size={26} weight="duotone" className="mt-1 shrink-0"/>
                  <div className="min-w-0 flex-1">
                    <p className="label-tiny mb-1">/{s.id}</p>
                    <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">{s.title}</h2>
                    <p className="text-sm md:text-base mt-2 text-[var(--brand-ink)]/85">{s.blurb}</p>

                    {/* Two-tool tabs explanation (used by AI Coach) */}
                    {s.twoTabs && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                        {s.twoTabs.map((t, i) => (
                          <div key={t.title} className="card-hard rounded-md p-4 bg-white" data-testid={`guide-${s.id}-tool-${i}`}>
                            <p className="font-display text-lg font-semibold leading-tight mb-1">{t.title}</p>
                            <p className="text-sm text-[var(--brand-ink)]/75">{t.desc}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <ul className="mt-4 space-y-1.5 text-sm">
                      {s.how.map((h, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="font-mono-d text-[var(--brand-muted)] mt-0.5">→</span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* Contact */}
        <section id="contact" data-testid="guide-contact" className="mt-12 scroll-mt-24">
          <div className="card-hard rounded-md p-6 bg-[var(--brand-ink)] text-white" style={{ background: "linear-gradient(135deg, #121212 0%, #1a1a1a 100%)" }}>
            <p className="label-tiny text-[var(--bolt-mustard)] mb-2">/contact</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">still stuck? we&apos;re actual humans.</h2>
            <p className="mt-2 text-white/75 max-w-2xl">
              feature ideas, bug reports, partnerships, or just want to say hi — drop us a line and we&apos;ll write back. messages land in <b className="text-white">jobvaultadmin@gmail.com</b>, usually answered within 48h.
            </p>
          </div>
          <div className="mt-4">
            <ContactForm/>
          </div>
        </section>

        {/* Bottom CTA */}
        <div className="mt-12 mb-8 flex items-center justify-between flex-wrap gap-4">
          <p className="font-mono-d text-xs text-[var(--brand-muted)]">made with 🧡 in the UK · for the chronically applying</p>
          <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="font-mono-d text-xs underline text-[var(--brand-muted)] hover:text-[var(--brand-ink)]">back to top ↑</a>
        </div>
      </div>
    </AppShell>
  );
}
